/**
 * Renders one panel from its definition and its result rows.
 *
 * Not every panel is a chart. A KPI is a number and a table is a table, and
 * drawing either through a charting library costs a canvas and gains nothing,
 * so only bar, line and pie reach ECharts.
 *
 * The chart instance is registered with the chart registry so an export can
 * ask for the exact pixels on screen. That is only possible because the
 * dashboard is drawn in our own document: a cross-origin frame cannot be
 * captured at all.
 */
import { BarChart, LineChart, PieChart } from "echarts/charts";
import {
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { CircleSlash, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { registerChart, unregisterChart } from "../services/chartRegistry";
import {
  axisTitle,
  buildOption,
  chronologicalRows,
  displayColumns,
  ECHARTS_TYPES,
  formatValue,
  humanise,
  memberLabel,
  type UnitMap,
} from "../services/chartOptions";
import {
  chartTheme,
  exportChartTheme,
  onThemeChange,
} from "../services/chartTheme";
import type { ChartType, PanelEncoding, PanelResult } from "../types";

// Registered once at module load. Importing the whole of ECharts would pull in
// every chart type and component for the three we actually draw.
echarts.use([
  BarChart,
  LineChart,
  PieChart,
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
]);

/** Rows beyond this make a table unreadable and the DOM slow. */
const TABLE_ROW_LIMIT = 100;

/** Rows sampled to decide whether a column holds figures. */
const TYPE_SAMPLE = 20;

const NO_ROWS: Record<string, unknown>[] = [];
const NO_COLUMNS: string[] = [];
const NO_UNITS: UnitMap = {};

/**
 * Whether a column holds figures, and so should be right-aligned.
 *
 * Numbers read as a column only when their digits line up, and the engine
 * returns measures as strings, so the declared type is no help. A sample is
 * enough: a column that is numeric for twenty rows and textual for the
 * twenty-first is a column with dirty data, not a column to left-align.
 */
function isNumericColumn(
  column: string,
  rows: Record<string, unknown>[],
): boolean {
  let seen = 0;
  for (const row of rows.slice(0, TYPE_SAMPLE)) {
    const value = row[column];
    if (value === null || value === undefined || value === "") continue;
    if (typeof value === "boolean" || !Number.isFinite(Number(value))) {
      return false;
    }
    seen += 1;
  }
  return seen > 0;
}

/**
 * A placeholder shaped like what is coming.
 *
 * Three grey lines stood in for every panel, so a bar chart and a KPI tile
 * loaded identically and the grid jumped when the real content arrived.
 */
function PanelSkeleton({ chartType }: { chartType: ChartType }) {
  const variant =
    chartType === "scalar" ? "scalar" : chartType === "table" ? "table" : "chart";
  const bars = variant === "chart" ? 7 : 3;
  return (
    <div
      className={`panel-skeleton ${variant}`}
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading panel data…</span>
      {Array.from({ length: bars }, (_, index) => (
        <i key={index} />
      ))}
    </div>
  );
}

interface ChartViewProps {
  panelId: string;
  chartType: ChartType;
  encoding: PanelEncoding;
  result: PanelResult | undefined;
  loading?: boolean;
}

export function ChartView({
  panelId,
  chartType,
  encoding,
  result,
  loading = false,
}: ChartViewProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const isChart = ECHARTS_TYPES.has(chartType);

  // The theme is a data attribute on the document, not React state, so it is
  // subscribed to rather than passed down. The snapshot is cached per theme,
  // which is what keeps it usable as a dependency below.
  const theme = useSyncExternalStore(onThemeChange, chartTheme, chartTheme);

  // Stable references. A `??` fallback allocates a new array on every render,
  // which would make the effect below tear the chart down and rebuild it each
  // time the parent re-rendered — visible as a flicker on every refresh tick.
  const rows = result?.rows ?? NO_ROWS;
  const columns = result?.columns ?? NO_COLUMNS;
  const units = result?.provenance?.units ?? result?.units ?? NO_UNITS;
  const hasData = rows.length > 0;

  const option = useMemo(
    () =>
      isChart && hasData
        ? buildOption(chartType, encoding, columns, rows, theme, units)
        : null,
    [isChart, hasData, chartType, encoding, columns, rows, theme, units],
  );

  useEffect(() => {
    if (!option || !mountRef.current) return;

    const chart = echarts.init(mountRef.current);
    chart.setOption(option);
    // The export variant is a closure rather than a built option: it is needed
    // only when someone exports, and building one per panel per theme change
    // would cost every reader for something most never do.
    registerChart(panelId, chart, option, () =>
      buildOption(chartType, encoding, columns, rows, exportChartTheme(), units),
    );

    // ResizeObserver rather than a window listener: a panel also changes size
    // when the grid reflows or a sibling is removed, neither of which fires a
    // window resize.
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(mountRef.current);

    return () => {
      observer.disconnect();
      unregisterChart(panelId);
      chart.dispose();
    };
  }, [panelId, option, chartType, encoding, columns, rows, units]);

  // No result at all means the panel has not run yet, which is not the same as
  // running and returning nothing. Saying "no data" here hid real failures.
  if ((loading && !hasData) || result === undefined) {
    return <PanelSkeleton chartType={chartType} />;
  }

  if (result?.status === "error") {
    return (
      <div className="panel-error" role="alert">
        <TriangleAlert size={19} aria-hidden="true" />
        <strong>This panel could not load.</strong>
        <span>{result.error?.message ?? "The query did not complete."}</span>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="panel-empty">
        <CircleSlash size={19} aria-hidden="true" />
        <span>No data in scope for this panel.</span>
      </div>
    );
  }

  if (chartType === "scalar") {
    // A KPI is a number, not a chart. Rendering it through a canvas would cost
    // a chart instance and lose selectable text.
    const declared = encoding.y.filter((column) => columns.includes(column));
    const figures = declared.length > 0 ? declared : columns;
    const headline = rows[0] ?? {};
    return (
      <div className="scalar-row">
        {figures.map((column) => (
          <div className="scalar" key={column}>
            <div className="scalar-value">
              {formatValue(headline[column], column)}
            </div>
            <div className="scalar-label">{memberLabel(column, units)}</div>
          </div>
        ))}
      </div>
    );
  }

  if (chartType === "table") {
    const visible = chronologicalRows(rows, columns).slice(0, TABLE_ROW_LIMIT);
    const tableColumns = displayColumns(columns);
    const numeric = new Set(
      tableColumns.filter((column) => isNumericColumn(column, rows)),
    );
    return (
      <div className="panel-table-wrap">
        <table className="panel-table">
          <thead>
            <tr>
              {tableColumns.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className={numeric.has(column) ? "numeric" : undefined}
                >
                  {memberLabel(column, units)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, index) => (
              <tr key={index}>
                {tableColumns.map((column) => (
                  <td
                    key={column}
                    className={numeric.has(column) ? "numeric" : undefined}
                  >
                    {formatValue(row[column], column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > visible.length && (
          <p className="panel-table-note">
            Showing {visible.length} of {rows.length.toLocaleString()} rows.
          </p>
        )}
      </div>
    );
  }

  const measures = encoding.y.length > 0 ? encoding.y : columns.slice(1);
  return (
    <div
      ref={mountRef}
      className="panel-chart"
      role="img"
      aria-label={`${chartType} chart of ${axisTitle(measures, units)}${
        encoding.x ? ` by ${humanise(encoding.x)}` : ""
      }`}
    />
  );
}
